import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useCrud } from '../../hooks/useApi';
import { identityService } from '../../services/api';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import CompanyModal from '../../components/Identity/CompanyModal';
import CompanyTable from '../../components/Identity/CompanyTable';
import CompanyFilters from '../../components/Identity/CompanyFilters';
import { isEmptyRichText } from '../../components/RichTextEditor';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { formatCnpj } from '../../utils/cnpj';

const companyApi = {
  getList: (params = {}) => identityService.getCompanies(params).then((res) => res.data ?? []),
  getItem: (id) => identityService.getCompany(id).then((r) => r.data),
  create: (data) => identityService.createCompany(data).then((r) => r.data),
  update: (id, data) => identityService.updateCompany(id, data).then((r) => r.data),
  delete: (id) => identityService.deleteCompany(id).then(() => {}),
};

const Companies = () => {
  const { t } = useTranslation('identity');

  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const { useList, useCreate, useUpdate, useDelete } = useCrud('companies', companyApi);
  const { data: rawCompanies = [], isLoading, error } = useList({});
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const companies = useMemo(
    () =>
      (rawCompanies || []).map((c) => ({
        ...c,
        status:
          c.status ??
          (c.is_active === false ? 'INACTIVE' : 'ACTIVE'),
      })),
    [rawCompanies],
  );

  const buildCompanyBody = (d, { forEdit }) => {
    const description =
      typeof d.description === 'string' && !isEmptyRichText(d.description) ? String(d.description).trim() : null;
    const base = {
      name: d.name.trim(),
      cnpj: formatCnpj(d.cnpj) || d.cnpj,
      email: (d.email || '').trim() || undefined,
      website: (d.website || '').trim() || undefined,
      address: (d.address || '').trim() || undefined,
      city: (d.city || '').trim() || undefined,
      state: (d.state || '').trim().substring(0, 2).toUpperCase() || undefined,
      zip_code: (d.zip_code || '').trim() || undefined,
      economic_activity_id: d.economic_activity_id || undefined,
      company_size_id: d.company_size_id || undefined,
      company_status_id: d.company_status_id || undefined,
      ...(description ? { description } : {}),
    };
    if (forEdit) {
      base.is_active = d.status !== 'INACTIVE';
    }
    return base;
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setShowModal(true);
  };

  const handleEdit = async (company) => {
    const row = companies.find((c) => String(c.id) === String(company.id)) || company;
    try {
      const { data } = await identityService.getCompany(company.id);
      setEditingCompany({ ...row, ...(data || {}) });
    } catch {
      setEditingCompany(row);
    }
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta empresa?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editingCompany) {
        await updateMutation.mutateAsync({ id: editingCompany.id, data: buildCompanyBody(data, { forEdit: true }) });
      } else {
        if (!data.email?.trim()) {
          toast.error(t('inviteEmailRequired'));
          return;
        }
        if (!formatCnpj(data.cnpj)) {
          toast.error('Informe um CNPJ válido com 14 dígitos.');
          return;
        }
        const inviteBody = {
          company: buildCompanyBody(data, { forEdit: false }),
          invite_email: data.email.trim(),
          login_with: 'email',
        };
        let res;
        try {
          res = await identityService.createCompanyWithAccessInvite(inviteBody).then((r) => r.data);
        } catch (inviteErr) {
          const created = await identityService.createCompany(buildCompanyBody(data, { forEdit: false }));
          res = { company: created.data, email_sent: false, message: userFacingError(inviteErr, 'saveError') };
        }
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        if (res?.email_sent) {
          toast.success(t('companyCreatedInvite'));
        } else {
          toast.success(t('companyCreatedActive'));
          if (res?.message) toast(res.message, { icon: 'ℹ️', duration: 6000 });
        }
      }
      setShowModal(false);
      setEditingCompany(null);
    } catch (err) {
      console.error(err);
      toast.error(userFacingError(err, 'saveError'));
    }
  };

  const filteredCompanies = companies.filter((company) => {
    const matchesSearch =
      !filters.search ||
      (company.name && company.name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (company.cnpj && String(company.cnpj).includes(filters.search));

    const matchesStatus = !filters.status || company.status === filters.status;
    const matchesType = !filters.type || company.type === filters.type;

    return matchesSearch && matchesStatus && matchesType;
  });

  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    const key = filters.sortBy;
    const aValue = a[key] ?? '';
    const bValue = b[key] ?? '';

    if (filters.sortOrder === 'asc') {
      return String(aValue).localeCompare(String(bValue));
    }
    return String(bValue).localeCompare(String(aValue));
  });

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("companiesTitle")}
          description="Cadastro de pessoas jurídicas no tenant — opcionalmente com usuário e convite por e-mail."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Identidade', to: '/identity/companies' },
            { label: 'Empresas' },
          ]}
        >
          <Can permission={P.IDENTITY_ACCESS}>
            <button type="button" onClick={handleCreate} className="btn btn-primary inline-flex gap-2 shadow-sm">
              <Plus className="h-4 w-4" />
              Nova empresa
            </button>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <CompanyFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          <div className="p-5 sm:p-6">
            <CompanyTable
              companies={sortedCompanies}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <CompanyModal
          company={editingCompany}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingCompany(null);
          }}
        />
      )}
    </FormPageShell>
  );
};

export default Companies;
